/**
 * مُسقِط البورتال — **وما يُعرَض يُعلَن، لا ما يُحجَب**.
 *
 * قاعدةُ v2 كانت «احذف الحقول الحسّاسة من الاستجابة»، **وكلُّ حقلٍ جديد يُولَد
 * مكشوفاً** حتى يتذكّر أحدٌ حذفه. وهنا تُقلَب: الاستجابةُ تُبنى من **قائمةٍ
 * بيضاء معلَنة**، فالحقلُ الجديد في الجدول **لا يخرج حتى يُضاف إليها**.
 *
 * وثلاثُ طبقاتٍ تحرس ذلك:
 *
 *  1. **القائمةُ نفسها ترفض اسماً يحمل هويةَ موظف** — عند تحميل الوحدة، لا في
 *     اختبارٍ يُنسى. فمن يكتب `authorId: true` لا يُقلِع نظامُه.
 *  2. **والفاعلُ لا يُمرَّر خاماً**: حقلٌ مُعلَنٌ `actor` يقبل الشكل المُقنَّع
 *     وحده — إمّا «الوكالة» صوتاً واحداً، أو اسمَ أحد ناس العميل. ومعرّفٌ خام
 *     في موضعه **يُرفَض وقت التشغيل**.
 *  3. **وما ليس في القائمة يسقط**، ولو أضافته القاعدةُ غداً.
 */
/**
 * يُقنِّع فاعلاً — **من موضعٍ واحد**.
 *
 * ولا يقرّر بالاسم ولا بالبادئة، بل بالعلاقة المُعلَنة: هل هذا الفاعل جهةُ
 * اتصالٍ في بورتال هذا العميل؟ فموظّفُ الوكالة — أيُّ موظف — يصير «الوكالة»،
 * **والنظام نفسه كذلك** حين لا فاعل.
 */
export function portalActor(input) {
    if (input.actorId === null || !input.actorIsPortalContact)
        return { kind: "agency" };
    return {
        kind: "client_person",
        name: input.actorName ?? "",
        isMe: input.actorId === input.viewerId,
    };
}
/** هل هذه القيمة فاعلٌ مُقنَّعٌ فعلاً؟ — يُسأل وقت الإسقاط، لا يُفترض */
function isPortalActor(value) {
    if (value === null || typeof value !== "object")
        return false;
    const kind = value.kind;
    if (kind === "agency")
        return Object.keys(value).length === 1;
    if (kind !== "client_person")
        return false;
    const row = value;
    return typeof row.name === "string" && typeof row.isMe === "boolean";
}
/**
 * **أسماءُ القوائم البيضاء، مُعلَنةٌ أوّلاً.**
 *
 * فالقوائمُ تتداخل — لوحةُ العنصر تحمل تعليقاتٍ وقرارات — فلو اشتُقّ النوعُ من
 * الجدول لرجع إلى نفسه. والإعلانُ المسبق يحلّها، **ويُلزِم بأن كلَّ عرضٍ له
 * قائمتُه**: اسمٌ هنا بلا شكلٍ هناك لا يُبنى.
 */
export const PORTAL_VIEW_NAMES = [
    "portal_plan",
    "portal_item",
    "portal_item_detail",
    "portal_part",
    "portal_media",
    "portal_decision",
    "portal_comment",
    "portal_notification",
    "portal_action",
    "portal_contact",
    "portal_push",
    "portal_report",
    "portal_publication",
    "portal_message",
    "portal_file",
    "portal_file_link",
    "portal_meeting",
];
/**
 * **القوائمُ البيضاء** — وهي العقد الكامل لما يراه العميل.
 *
 * ولا نبرةَ صوتٍ ولا بريفَ ولا موادَّ ولا تعليقاً داخلياً: تلك داخليةٌ بطبعها.
 * **والحضورُ ممنوعٌ نهائياً** (§7) — لا نقطةَ ولا بطاقة، فظهورُها تسريبُ هوية.
 */
export const PORTAL_VIEWS = {
    /** خطةُ العميل — غلافٌ لعناصره، بلا شيءٍ عن من بناها */
    /**
     * **تقريرٌ كما يقرؤه العميل — وأرقامُه له وحده.**
     *
     * **ولا مراجعين في الجواب**: من كتبه ومن وافق عليه شأنٌ داخليّ. والعميلُ
     * يرى التقريرَ ومتى وصله — **وهويةُ موظّفٍ تُسرَّب ولو باسمٍ وحده**.
     */
    /**
     * **صفُّ النشر كما يراه صاحبُه** — موعدٌ وحالةٌ وإثبات.
     *
     * **ولا مَن وسمه**: من ضغط «نُشر» موظّفٌ في الوساطة، **والوكالةُ صوتٌ واحد**.
     * فالعميلُ يرى أن شغلَه نزل ومتى وأين، لا من ضغط الزرّ.
     */
    /**
     * **رسالةٌ في محادثة العميل — وفاعلُها مُقنَّع.**
     *
     * فـ`author` يمرّ بـ`portalActor()`: **ناسُ العميل بأسمائهم، والوكالةُ
     * «الوكالة»**. والإسقاطُ **يرفض فاعلاً غيرَ مُقنَّع** صراحةً — فلا يُمرَّر
     * معرّفٌ خامٌ ولا يُحذَف الحقلُ بصمت.
     */
    portal_message: {
        id: true,
        body: true,
        createdAt: true,
        editedAt: true,
        deleted: true,
        author: { actor: true },
        attachments: { view: "portal_file", many: true },
    },
    /**
     * **ملفٌّ في خيط العميل — باسمه وحالة فاحصه** (قرار شادي ٢٠٢٦-١١-٠١).
     *
     * **ولا مفتاحَ تخزينٍ ولا من رفعه**: المفتاحُ عنوانُ البايتات في درجنا، ومن
     * رفعه من عندنا هويةُ موظّف — **والوكالةُ صوتٌ واحد**. والرابطُ لا يُحمَل هنا
     * بل يُطلَب لحظةَ النقر (`portal_file_link`).
     */
    portal_file: {
        id: true,
        filename: true,
        contentType: true,
        sizeBytes: true,
        scan: true,
    },
    /** **ورابطُه الموقَّعُ واسمُه** — لا شيءَ غيرهما */
    portal_file_link: {
        url: true,
        filename: true,
    },
    /**
     * **اجتماعُه كما يراه — ولا رابطٌ قبل التأكيد** (§3).
     *
     * فرابطٌ يُعرَض على موعدٍ **مقترَحٍ** يجعله يدخل غرفةً لا أحدَ فيها. والحقلُ
     * في القائمة البيضاء (فالمؤكَّدُ يحتاجه)، **والخدمةُ تُفرِّغه فيما لم يُؤكَّد
     * بعد** — موضعٌ واحدٌ يقرؤه السردُ وحده، وبوّابةٌ تُثبت أنه لا يسافر.
     *
     * **ولا هويةَ موظّفٍ**: من اقترح من عندنا شأنٌ داخليّ — **والوكالةُ صوتٌ
     * واحد**. وجهةُ الاقتراح تكفي ليعرف العميلُ أن الكرةَ عنده أو عندنا.
     */
    portal_meeting: {
        id: true,
        subject: true,
        state: true,
        startsAt: true,
        durationMinutes: true,
        proposedBy: true,
        link: true,
        reason: true,
        createdAt: true,
        allowedActions: true,
    },
    portal_publication: {
        id: true,
        platform: true,
        status: true,
        publishAt: true,
        publishedAt: true,
        proofUrl: true,
        /**
         * **وعنوانُ عنصره حقلٌ لا إسقاطٌ متداخل.**
         *
         * فإسقاطُ العنصر يُلزم بـ«هل ينتظر قراري» — **حسابٌ لا معنى له لصفّ نشر**:
         * ما نُشر لا ينتظر قراراً. وإلزامُ القارئ بحسابه كان سيعني رقماً يُحسَب
         * ليُطرَح.
         */
        itemTitle: true,
        /**
         * **وطلبُ الإزالة بحالِه وجوابِه** — تبويبُ «المنشورات» (قرار شادي
         * ٢٠٢٦-١٠-٢٦). وحقولُه أربعة: الحالُ والجوابُ ووقتاه — **ولا هويةَ من أجاب**.
         */
        takedown: true,
        canRequestTakedown: true,
    },
    portal_report: {
        id: true,
        title: true,
        body: true,
        periodMonth: true,
        publishedAt: true,
        /**
         * **وطلبُ التعديل يُعاد إليه** — فمن طلب يعرف أن طلبَه وصل.
         *
         * **وسببُه نصُّه هو** كتبه بنفسه، فلا تسريبَ فيه. ومن طلبته زميلتُه يرى أن
         * الطلبَ قائمٌ فلا يطلب مرّةً أخرى — **وهو قرارُ «ناسُ العميل بأسمائهم»**
         * بتطبيقه الأدنى: لا اسمَ موظفٍ، ونعم لمعرفة أن شيئاً يجري.
         */
        editRequested: true,
        editReason: true,
    },
    portal_plan: {
        id: true,
        name: true,
        period: true,
        startsOn: true,
        endsOn: true,
        items: { view: "portal_item", many: true },
    },
    /** عنصرٌ في قائمة — ما يكفي للبطاقة، لا أكثر */
    portal_item: {
        id: true,
        title: true,
        typeLabelAr: true,
        typeLabelEn: true,
        state: true,
        publishAt: true,
        platforms: true,
        awaitingMyDecision: true,
        /** **واسمُ خطته** — وهو في شاشة خططه أصلاً */
        planName: true,
        /** **ورابطُ معاينته الموقَّت** — كفرُه أو معاينتُه، مما يراه في لوحته؛ لا مفتاحَ تخزين */
        preview: true,
        /** **ومنذ متى ينتظر قراره** — «أقدمها مستنّي من يومين» */
        waitingSince: true,
    },
    /** لوحةُ العنصر — المعاينةُ والقرارُ وتاريخُه */
    portal_item_detail: {
        id: true,
        version: true,
        title: true,
        typeLabelAr: true,
        typeLabelEn: true,
        state: true,
        publishAt: true,
        platforms: true,
        caption: true,
        altCaption: true,
        awaitingMyDecision: true,
        /** **ما يجوز لي على هذا السجل يقوله السيرفر** — لا تحسبه الشاشة */
        allowedActions: true,
        revisionsUsed: true,
        revisionLimit: true,
        exceptionPending: true,
        /**
         * **أجزاءُ العنصر — الريل والكفر** — ليختار العميلُ ما يرجع منها.
         *
         * **أسماءُ المهامّ وحدها، لا أصحابُها ولا مواعيدُها ولا حالُها الداخليّ**:
         * فالعميلُ يقرّر على الشغل، لا على من في الوكالة يشتغله.
         */
        parts: { view: "portal_part", many: true },
        media: { view: "portal_media", many: true },
        decisions: { view: "portal_decision", many: true },
        comments: { view: "portal_comment", many: true },
    },
    /** جزءٌ من العنصر — اسمُه، **وهل وافق عليه العميل في جولةٍ سابقة** فلا يُعاد عرضُه */
    portal_part: {
        id: true,
        nameAr: true,
        nameEn: true,
        isCover: true,
        approved: true,
    },
    /** وسيطٌ للمعاينة — رابطٌ موقَّت، ولا اسمَ من رفعه */
    portal_media: {
        id: true,
        /** **الجزءُ الذي هذه معاينتُه** — أو لا شيء لوسيط العنصر كلّه (الشرائح) */
        partId: true,
        kind: true,
        url: true,
        order: true,
        fileName: true,
        sizeBytes: true,
        /**
         * **ونوعُه، فالمعاينةُ تُرى لا تُحمَّل.**
         *
         * وبلا هذا الحقل كانت اللوحةُ تعرض **رابطاً** لكل وسيط — وأوّلُ ما يفتح
         * العميلُ البورتال من أجله **أن يرى البوست**، لا أن ينزّله ثم يفتحه. ولا
         * يحمل النوعُ هويةَ أحد: `image/png` لا تقول من رفعها.
         */
        contentType: true,
    },
    /** قرارٌ سابق — **وصاحبُه بالاسم إن كان من ناس العميل** */
    portal_decision: {
        id: true,
        decision: true,
        comment: true,
        cycle: true,
        at: true,
        by: { actor: true },
    },
    /** تعليقٌ على عنصر — يراه الفريق والعميل، والوكالةُ فيه صوتٌ واحد */
    portal_comment: {
        id: true,
        body: true,
        at: true,
        editedAt: true,
        by: { actor: true },
    },
    /**
     * **إشعارٌ في صندوقي** — نصُّه ورابطُه وقيمُه، **ولا من سبّبه**.
     *
     * وحقولُها بأسماء الصفّ نفسها، فيُسقَط نفسُ ما يبنيه المسارُ للموظّف — **ويسقط
     * منه `actor`**. وفي v2 كان الصندوقُ واحداً للجميع، فاسمُ الموظّف يصل جهةَ
     * الاتصال في كل سطر.
     */
    /**
     * **ونصُّه محلولٌ — لا مفتاحاً ولا قيمَ مِلء.**
     *
     * **وكانت تُعلن `titleKey`/`bodyKey`/`params`** بعد أن صار السيرفرُ يحلّ
     * النصَّ: **فقائمةٌ بيضاء تُسقِط ما لا تعرفه**، فخرج صندوقُ العميل **بلا
     * نصٍّ أصلاً** — سبعةُ حقولٍ ولا جملةٌ فيها. وذلك هو ثمنُ القائمة البيضاء
     * وفضلُها معاً: **آمنةٌ بالغياب، فتحتاج أن تُحدَّث مع ما تصفه** —
     * **واختبارٌ يقابل ما يُنتَج بما يُعلَن** هو ما أظهره.
     *
     * **ولا `params`**: فالقيمُ مملوءةٌ في النصّ، **وإرسالُها ثانيةً سطحُ
     * تسريبٍ بلا فائدة** — وقد تحمل اسمَ موظّفٍ يُسقِطه `actor` من بابه.
     */
    portal_notification: {
        id: true,
        event: true,
        severity: true,
        link: true,
        readAt: true,
        createdAt: true,
        title: true,
        body: true,
    },
    /**
     * **بندُ إجراءٍ كما يراه العميل** — ولا اسمَ عميلٍ فيه ولا هويةَ موظف.
     *
     * **والبندُ محسوبٌ لا مخزَّن**، فليس له صفٌّ يُسقَط: **يُسقَط ما تبنيه
     * الخدمةُ منه**. و`clientName` غيرُ مُعلَنةٍ هنا بقصد — **فهو عميلُ نفسه**،
     * واسمُه في وجهه حشوٌ، **ولو حمل غيرَه لكان تسريباً**.
     */
    portal_action: {
        kind: true,
        entityId: true,
        subject: true,
        since: true,
        link: true,
        until: true,
    },
    /** زملائي في البورتال — **بأسمائهم** (قرار شادي) */
    portal_contact: {
        name: true,
        isMe: true,
        isPrimary: true,
    },
    /** اشتراكُ دفعٍ لجهاز — ولا مفاتيحَ في الجواب */
    portal_push: {
        id: true,
        deviceLabel: true,
        at: true,
        isThisDevice: true,
    },
};
/**
 * **أسماءُ الحقول التي تحمل هويةَ موظف** — ولا تُقبَل في قائمةٍ بيضاء أبداً.
 *
 * ويُفحَص هذا **عند تحميل الوحدة** لا في اختبارٍ يُنسى: فمن يكتب `authorId: true`
 * في قائمةٍ بورتالية **لا يُقلِع نظامُه**، ويقرأ السبب في الرسالة.
 *
 * والفاعلُ ليس استثناءً من القاعدة بل تطبيقٌ لها: يُعلَن `{ actor: true }`
 * فيُقنَّع ويُفحَص شكلُه، ولا يمرّ معرّفٌ خام تحت أي اسم.
 */
const IDENTITY_BEARING = [
    "authorid",
    "actorid",
    "userid",
    "createdby",
    "createdbyid",
    "uploadedby",
    "uploadedbyid",
    "recordedby",
    "recordedbyid",
    "assignedby",
    "assignees",
    "assigneeids",
    "requestedby",
    "resolvedby",
    "archivedby",
    "finishedby",
    "email",
    "avatar",
    "photo",
    "initials",
    "passwordhash",
    "mentions",
    "presence",
    "lastseenat",
    "internalnote",
    "internalcomment",
    "brief",
    "tone",
    "tonofvoice",
];
function assertNoLeakyName(view, field) {
    const needle = field.toLowerCase();
    for (const banned of IDENTITY_BEARING) {
        if (needle === banned || needle.endsWith(banned)) {
            throw new Error(`Portal view "${view}" declares field "${field}", which carries internal identity. ` +
                `The portal shows the agency as one voice and the client's own people by name — ` +
                `declare "{ actor: true }" and build it with portalActor(), never a raw id or name.`);
        }
    }
}
for (const [view, shape] of Object.entries(PORTAL_VIEWS)) {
    for (const [field, spec] of Object.entries(shape)) {
        if (spec === true)
            assertNoLeakyName(view, field);
    }
}
/**
 * يُسقِط سجلّاً واحداً بقائمته.
 *
 * وما ليس في القائمة يسقط، **ولو أضافته القاعدةُ غداً**. وحقلٌ مُعلَنٌ غائبٌ
 * من السجل لا يُختلَق: يُحذَف من الجواب، فلا تُرسَل `undefined` في JSON.
 */
export function projectPortal(view, row) {
    if (row === null || typeof row !== "object") {
        throw new Error(`Portal projection of view "${view}" expected a record, got ${typeof row}.`);
    }
    const source = row;
    const shape = PORTAL_VIEWS[view];
    const out = {};
    for (const [field, spec] of Object.entries(shape)) {
        if (!(field in source))
            continue;
        const value = source[field];
        if (spec === true) {
            out[field] = value;
            continue;
        }
        if ("actor" in spec) {
            /**
             * **فاعلٌ غيرُ مُقنَّع يُرفَض** — لا يُمرَّر ولا يُحذَف بصمت.
             *
             * فالحذفُ الصامت يُنتج لوحةً بلا «من وافق»، والتمريرُ يُنتج التسريب. وكلٌّ
             * منهما عطبٌ، فالصواب أن يسقط المسار بصوتٍ عالٍ في أوّل اختبار.
             */
            if (!isPortalActor(value)) {
                throw new Error(`Portal view "${view}" field "${field}" must be built with portalActor(); ` +
                    `got ${JSON.stringify(value)?.slice(0, 80) ?? "undefined"}.`);
            }
            out[field] = value;
            continue;
        }
        if (value === null) {
            out[field] = null;
            continue;
        }
        if (spec.many === true) {
            if (!Array.isArray(value)) {
                throw new Error(`Portal view "${view}" field "${field}" is declared as many, got one.`);
            }
            out[field] = value.map((entry) => projectPortal(spec.view, entry));
            continue;
        }
        out[field] = projectPortal(spec.view, value);
    }
    return out;
}
/** يُسقِط قائمة — نفسُ القائمة البيضاء، ولا نسخةَ ثانية منها */
export function projectPortalMany(view, rows) {
    return rows.map((row) => projectPortal(view, row));
}
/** حقولُ عرضٍ ما، بأسمائها — تقرؤها البوابات والاختبارات */
export function portalViewFields(view) {
    return Object.keys(PORTAL_VIEWS[view]);
}
//# sourceMappingURL=projection.js.map